import { useEffect } from 'react';
import { Howler } from 'howler';

const ALLOWED_ORIGIN = 'https://yatsu.games';

// Module-level state shared with the play handlers.
// We only auto-unmute on the first user play action when the game *booted*
// muted (?muted=1). If the parent (yatsu.games) explicitly toggles mute via
// postMessage, that is treated as user/parent-controlled and we never override it.
let bridgeActive = false;
let autoUnmutePending = false;

/**
 * Call when the user presses play / shoot. If the game booted muted (and the
 * parent hasn't since taken control of the mute state), this unmutes audio once.
 * No-op everywhere else (e.g. non-demo routes where the bridge isn't mounted).
 */
export const unmuteOnUserPlay = () => {
  if (!bridgeActive || !autoUnmutePending) return;
  autoUnmutePending = false;
  Howler.mute(false);
};

/**
 * Renderless bridge for embedding in yatsu.games.
 * - Reads `?muted=1` at boot (marks audio for auto-unmute on first play).
 * - Listens for postMessage `{ type: 'yatsu:setMuted', muted: boolean }`
 *   from the yatsu.games parent and mutes/unmutes audio globally via Howler.
 */
export const YatsuMuteBridge: React.FC = () => {
  useEffect(() => {
    bridgeActive = true;

    const mutedAtBoot = new URLSearchParams(window.location.search).get('muted') === '1';
    if (mutedAtBoot) {
      Howler.mute(true);
      autoUnmutePending = true;
    }

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== ALLOWED_ORIGIN) return;
      if (e.data?.type === 'yatsu:setMuted') {
        Howler.mute(!!e.data.muted);
        // Parent/user-triggered: stop auto-unmuting on play.
        autoUnmutePending = false;
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      // Restore on unmount so leaving the demo route doesn't leave audio muted.
      Howler.mute(false);
      autoUnmutePending = false;
      bridgeActive = false;
    };
  }, []);

  return null;
};
